import { child, push, ref, set, update } from "firebase/database";
import { httpsCallable } from "firebase/functions";
import { useListVals, useObjectVal } from "react-firebase-hooks/database";
import { fbDatabase, fbFunctions } from "../firebase";
import { sessionTransform } from "./session.types";
import type { SessionData } from "./session.types";

export * from "./session.types";
export { getPresentationItems, getNextPresentedItemUid, getPrevPresentedItemUid, calcWeightedProgress } from "./presentation";

const sessionOptions = {
  keyField: 'uid',
  transform: sessionTransform,
};

export function refSessions(boardUid: string) { return ref(fbDatabase, `sessions/${boardUid}`) }
export function refSession(boardUid: string, sessionUid: string) { return child(refSessions(boardUid), sessionUid) }

export function useSessions(boardUid: string) { return useListVals<SessionData>(refSessions(boardUid), sessionOptions) }
export function useSession(boardUid: string, sessionUid: string) { return useObjectVal<SessionData>(refSession(boardUid, sessionUid), sessionOptions) }

export const createSession: (boardUid: string, isTimed: boolean) => Promise<string | null> = async (boardUid, isTimed) => {
  const newSessionRef = await push(refSessions(boardUid));
  if (!newSessionRef.key) return null;

  const epoch = Date.now();
  await set(newSessionRef, {
    boardUid: boardUid,
    createdDate: epoch,
    isTimed: isTimed,
  });

  return newSessionRef.key;
}

export async function startSession(
  boardUid: string,
  sessionUid: string,
  firstItemUid: string,
): Promise<void> {
  const now = Date.now();
  await update(refSession(boardUid, sessionUid), {
    startTime: now,
    presentedUid: firstItemUid,
    presentedTime: now,
    endTime: null,
  });
}

export async function completeSession(
  boardUid: string,
  sessionUid: string,
): Promise<void> {
  const now = Date.now();
  await update(refSession(boardUid, sessionUid), {
    endTime: now,
    presentedUid: null,
  });
}

export async function cloneSession(
  boardUid: string,
  sessionUid: string,
): Promise<string | null> {
  const fn = httpsCallable<
    { boardUid: string; sessionUid: string },
    { sessionUid: string }
  >(fbFunctions, "cloneSession");
  const result = await fn({ boardUid, sessionUid });
  return result.data.sessionUid;
}

export async function updateSessionOrder(
  boardUid: string,
  sessionUid: string,
  order: string[],
): Promise<void> {
  await update(refSession(boardUid, sessionUid), {
    order: order,
  });
}

export async function presentItem(
  boardUid: string,
  sessionUid: string,
  itemUid: string,
): Promise<void> {
  const now = Date.now();
  await update(refSession(boardUid, sessionUid), {
    presentedUid: itemUid,
    presentedTime: now,
  });
}

export async function setSessionTargetTime(
  boardUid: string,
  sessionUid: string,
  minutes: number,
): Promise<void> {
  await update(refSession(boardUid, sessionUid), {
    targetTime: minutes,
  });
}

export async function editSessionIsTimed(
  boardUid: string,
  sessionUid: string,
  isTimed: boolean,
): Promise<void> {
  await update(refSession(boardUid, sessionUid), {
    isTimed: isTimed,
  });
}

export async function shredSession(boardUid: string, sessionUid: string): Promise<void> {
  const fn = httpsCallable<{ boardUid: string; sessionUid: string }, { success: boolean }>(fbFunctions, "shredSession");
  await fn({ boardUid, sessionUid });
}
